import SwitchActivePair from '@/components/common/SwitchActivePair';
import ModuleWindow, { moduleHeaderClassName } from '@/components/mosaic/ModuleWindow';
import ModuleWindowControls from '@/components/mosaic/ModuleWindowControl';

import { addMarketTob } from '@/services/MarketService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectActiveMarketPair, selectMarketPairs } from '@/state/reducers/marketPairSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { removeOrderConfig, selectOrderConfigs, setOrderConfigs } from '@/state/reducers/workspaceSlice';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { tabIcon } from '@/utils/hooks/useXplorSpotLayout';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { useMemo } from 'react';
import { isTablet } from 'react-device-detect';
import OrderExecution from './OrderExecution';

const OrderModule = (props: any) => {
    const { id, tradingModule } = props;

    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const screen = useScreenSizes();

    const pairs = useAppSelector(selectMarketPairs);
    const activePair = useAppSelector(selectActiveMarketPair);
    const accountType = useAppSelector(selectAccountType);
    const orderConfigs = useAppSelector(selectOrderConfigs);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const orderInstrument = useMemo(() => (orderConfigs && orderConfigs[id]) || undefined, [orderConfigs]);

    const instrument = useMemo(() => {
        return orderInstrument || activePair;
    }, [orderInstrument, activePair]);

    const instrumentOptions = useMemo(() => {
        return pairs.map((instrument) => {
            const config: any = getInstrumentConfig(accountType, instrumentConfigs, instrument.celer);

            const show = config?.type === 'Future' && config.gcexSymbol ? config.gcexSymbol : instrument.show;

            return {
                label: show,
                value: instrument.celer,
                market: instrument,
                config
            };
        });
    }, [pairs, instrumentConfigs, accountType]);

    return (
        <ModuleWindow
            {...props}
            renderToolbar={() => (
                <div className={moduleHeaderClassName} dir={lang.dir}>
                    <div className="h-full flex items-center p-2 gap-2">
                        {tabIcon[tradingModule?.key || ''] && (
                            <div className="flex items-center justify-center bg-brand-neutral-700 rounded-full p-1">
                                {tabIcon[tradingModule?.key || '']({
                                    className: 'w-4 h-4',
                                    'aria-hidden': true
                                })}
                            </div>
                        )}
                        <span className="font-semibold whitespace-nowrap">{tradingModule.title}</span>
                    </div>
                    <div className="flex h-full items-center">
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <SwitchActivePair
                                selected={{
                                    label: instrument?.show || lang.tickers.instrumentSelector,
                                    value: instrument?.celer || undefined,
                                    market: undefined
                                }}
                                options={instrumentOptions}
                                onSubmit={(instrument) => {
                                    dispatch(setOrderConfigs({ key: id, config: instrument }));
                                }}
                            />
                        )}
                        <ModuleWindowControls
                            {...props}
                            onClose={() => {
                                dispatch(removeOrderConfig({ key: id }));
                                addMarketTob(instrument.celer);
                            }}
                        />
                    </div>
                </div>
            )}>
            <div id="orderform" className="h-full bg-brand-background">
                <OrderExecution instrument={instrument} />
            </div>
        </ModuleWindow>
    );
};

export default OrderModule;
